// @givmo/passkeys — the passkey SIGN-IN transport (AS-WEB2's `PasskeyAssertionTransport` adapter,
// lifted from `apps/admin` by AS-WEB12 so the charity console's director door rides the same one).
//
// The single transport seam between `@givmo/auth`'s passkey sign-in primitive and the backend's two
// assertion routes: `@givmo/auth` owns the ceremony orchestration and the error model; this file only
// moves bytes.
//
// ⚠ NEITHER ROUTE IS IN THE COMMITTED OPENAPI SNAPSHOT. `packages/api-client/openapi.json` carries the
// AUTHENTICATED passkey-management surface (`/users/me/passkeys*`) but no `/authn/*` path at all — the
// backend mounts `/authn` as its own sub-app, whose document (`openapi.authn.json`) is a SEPARATE
// export the root snapshot never absorbs, exactly as `/connect/*` is. So this is not a stale snapshot
// and no regen will produce these operations; `agent_docs/api-client.md` § *A whole ROUTE the snapshot
// lacks* is the sanctioned disposition, and `givmoClient.post({ url })` is the same
// `@hey-api/client-fetch` client the generated ops bind to — the request inherits the whole
// interceptor stack (App Check, `X-Request-Id`, envelope unwrap, `{errors:[…]}` → `GivmoError`).
//
// ⚠ `/authn` IS UNAUTHENTICATED, and that is the point: nobody is signed in yet. The Bearer
// interceptor omits the header whenever `auth.currentUser` is null (each host's `lib/api-client.ts`),
// which is the state of every sign-in screen — so nothing needs to strip it. A stray token from an
// already-signed-in visitor is inert: the routes declare no auth dependency and never read it.
//
// ⚠ THE SUCCESS BODY OF THE EXCHANGE IS A BEARER CREDENTIAL. `custom_token` is returned to the caller
// and nothing else: it is never stored, never put on an error, and never handed to a decode helper
// that something else might log. The api-client's own logger is structurally incapable of receiving it
// (`logging.ts` takes metadata only, never bodies), and this file adds no logging of its own.

import { givmoClient } from '@givmo/api-client';
import {
  PasskeyTransportError,
  type PasskeyAssertionJson,
  type PasskeyAssertionTransport,
} from '@givmo/auth';
import {
  extractCustomToken,
  extractPasskeyOptions,
  toPasskeyTransportError,
} from './passkey-transports';

/** `POST` → the raw `PublicKeyCredentialRequestOptionsJSON`, wrapped in a one-key object. */
const ASSERTION_OPTIONS_URL = '/authn/passkeys/assertion/options';
/** `POST` → `{ custom_token }`. */
const ASSERTION_URL = '/authn/passkeys/assertion';

/** Generic copy for a refusal the backend sent no sentence with (e.g. a bare 404 while dark). */
const REFUSED_MESSAGE = 'Could not complete the passkey sign-in.';

/**
 * Build the transport. Stateless — a module-level constant would be equivalent, but a factory keeps
 * it symmetrical with the other ports and lets a test inject its own without module mocking.
 *
 * A body that carries no usable options document (`extractPasskeyOptions`: nested under `options`,
 * or the bare document) is a refusal, so the probe reads it as "not available" rather than rendering
 * a control that would fail at the first click.
 */
export function createPasskeyAssertionTransport(): PasskeyAssertionTransport {
  return {
    async requestAssertionOptions(init) {
      const { data, error } = await givmoClient.post({
        url: ASSERTION_OPTIONS_URL,
        signal: init?.signal,
      });
      if (error !== undefined && error !== null) {
        throw toPasskeyTransportError(error, REFUSED_MESSAGE);
      }
      const options = extractPasskeyOptions(data);
      if (options === null) {
        throw new PasskeyTransportError({ message: REFUSED_MESSAGE, status: 200 });
      }
      return options;
    },

    async exchangeAssertion(assertion: PasskeyAssertionJson, init) {
      const { data, error } = await givmoClient.post({
        url: ASSERTION_URL,
        body: { credential: assertion },
        signal: init?.signal,
      });
      if (error !== undefined && error !== null) {
        throw toPasskeyTransportError(error, REFUSED_MESSAGE);
      }
      const customToken = extractCustomToken(data);
      if (customToken === null) {
        throw new PasskeyTransportError({ message: REFUSED_MESSAGE, status: 200 });
      }
      return customToken;
    },
  };
}
